import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Loader2, Plus, RefreshCw } from 'lucide-react';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { PatientCard } from '@/components/medic/PatientCard';
import { TabBar } from '@/components/ui/TabBar';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { useAuthStore } from '@/store/authStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
  APPOINTMENT_DOCUMENT, PSY_NOTES,
  appointmentFromListItem, appointmentInitialData, appointmentPeriodFilters, countAppointments,
  loadAppointmentEnumLabels, loadAppointmentLinks, loadAppointments, loadCabinetAvailability,
  type AppointmentCounts, type AppointmentLinks, type AppointmentPeriod, type CabinetAppointment,
} from '@/lib/cabinet/appointments';
import type { RelatedNote } from '@/lib/cabinet/appointments';
import {
  CabinetAppointmentCard,
} from '@/components/cabinet/tabs/CabinetAppointmentCard';
import {
  CabinetAppointmentRelatedDialog, type AppointmentRelatedMode,
} from '@/components/cabinet/tabs/CabinetAppointmentRelatedDialog';

type Availability = 'loading' | 'ready' | 'missing' | 'error';

interface EditorState {
  objectType: 'document' | 'catalog';
  objectName: string;
  objectId?: string;
  initialData?: Record<string, unknown>;
}

function StateBox({ title, text, onRetry }: { title: string; text: string; onRetry?: () => void }) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-lg space-y-2 text-center">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{text}</div>
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry}>
            <RefreshCw className="mr-1 h-3 w-3" />
            Повторить
          </Button>
        )}
      </div>
    </div>
  );
}

export function CabinetAppointmentsTab() {
  const user = useAuthStore((s) => s.user);
  const [availability, setAvailability] = useState<Availability>('loading');
  const [availabilityError, setAvailabilityError] = useState('');
  const [psyInstalled, setPsyInstalled] = useState(false);
  const [statusLabels, setStatusLabels] = useState<Record<string, string>>({});
  const [typeLabels, setTypeLabels] = useState<Record<string, string>>({});

  const [period, setPeriod] = useState<AppointmentPeriod>('today');
  const [patient, setPatient] = useState<{ id: string; name: string } | null>(null);
  const [selected, setSelected] = useState<CabinetAppointment | null>(null);
  const [listKey, setListKey] = useState(0);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [cardPatient, setCardPatient] = useState<{ id: string; name: string } | null>(null);
  const [relatedMode, setRelatedMode] = useState<AppointmentRelatedMode | null>(null);

  const [counts, setCounts] = useState<AppointmentCounts | null>(null);
  const [countsError, setCountsError] = useState('');

  const [links, setLinks] = useState<AppointmentLinks | null>(null);
  const [linksError, setLinksError] = useState('');
  const [linksKey, setLinksKey] = useState(0);

  const countsLoading = counts === null && !countsError;
  const linksLoading = Boolean(selected && psyInstalled && !links && !linksError);

  const checkAvailability = useCallback(async () => {
    try {
      const result = await loadCabinetAvailability();
      setAvailabilityError('');
      setPsyInstalled(result.psy);
      if (!result.core) {
        setAvailability('missing');
        return;
      }
      setAvailability('ready');
      try {
        const labels = await loadAppointmentEnumLabels();
        setStatusLabels(labels.status);
        setTypeLabels(labels.type);
      } catch (e) {
        logErrorToJournal(e, 'Записи: подписи статусов приёма');
      }
    } catch (e) {
      logErrorToJournal(e, 'Записи: проверка ядра «ГидраМедик»');
      setAvailabilityError(friendlyError(e));
      setAvailability('error');
    }
  }, []);

  useEffect(() => {
    void checkAvailability();
  }, [checkAvailability]);

  const retryAvailability = () => {
    setAvailability('loading');
    setAvailabilityError('');
    void checkAvailability();
  };

  const refreshCounts = useCallback(async () => {
    if (availability !== 'ready') return;
    try {
      const rows = await loadAppointments(patient?.id);
      setCounts(countAppointments(rows));
      setCountsError('');
    } catch (e) {
      logErrorToJournal(e, 'Записи: счётчики периодов');
      setCountsError(friendlyError(e));
    }
  }, [availability, patient]);

  useEffect(() => {
    void refreshCounts();
  }, [refreshCounts, listKey]);

  useEffect(() => {
    if (!selected || !psyInstalled) return;
    let cancelled = false;
    loadAppointmentLinks(selected)
      .then((data) => {
        if (!cancelled) setLinks(data);
      })
      .catch((e) => {
        if (cancelled) return;
        logErrorToJournal(e, `Записи: связанные данные приёма ${selected.id}`);
        setLinksError(friendlyError(e));
      });
    return () => { cancelled = true; };
  }, [selected, psyInstalled, linksKey]);

  const filters = useMemo(
    () => appointmentPeriodFilters(period),
    [period],
  );
  const serverFilters = useMemo(
    () => (patient ? [{ field: 'patient_id', condition: '=' as const, value: patient.id }] : undefined),
    [patient],
  );

  const periodTabs = useMemo(() => {
    const suffix = (value: number) => (counts ? ` (${value})` : '');
    return [
      { id: 'today', label: `Сегодня${suffix(counts?.today || 0)}` },
      { id: 'upcoming', label: `Предстоящие${suffix(counts?.upcoming || 0)}` },
      { id: 'past', label: `Прошлые${suffix(counts?.past || 0)}` },
    ];
  }, [counts]);

  const periodCount = countsError || !counts
    ? null
    : period === 'today' ? counts.today : period === 'upcoming' ? counts.upcoming : counts.past;

  const changePeriod = (next: AppointmentPeriod) => {
    setPeriod(next);
    setSelected(null);
    setLinks(null);
    setLinksError('');
    setListKey((k) => k + 1);
  };

  const refresh = useCallback(() => {
    setSelected(null);
    setLinks(null);
    setLinksError('');
    setCounts(null);
    setCountsError('');
    setListKey((k) => k + 1);
  }, []);

  const openCreate = useCallback(() => {
    setEditor({
      objectType: 'document',
      objectName: APPOINTMENT_DOCUMENT,
      initialData: appointmentInitialData(patient, String(user?.id || '')),
    });
  }, [patient, user?.id]);

  const openEdit = useCallback((appointment: CabinetAppointment) => {
    if (!appointment.id) return;
    setEditor({ objectType: 'document', objectName: APPOINTMENT_DOCUMENT, objectId: appointment.id });
  }, []);

  const closeEditor = () => {
    const current = editor;
    setEditor(null);
    if (!current) return;
    if (current.objectName === APPOINTMENT_DOCUMENT) {
      setSelected(null);
      setLinks(null);
      setLinksError('');
      setListKey((k) => k + 1);
      return;
    }
    if (current.objectName === PSY_NOTES) {
      setLinks(null);
      setLinksError('');
      setLinksKey((k) => k + 1);
    }
  };

  const selectRow = (row: Record<string, unknown>) => {
    setLinks(null);
    setLinksError('');
    setSelected(appointmentFromListItem(row));
  };

  const filterPatient = () => {
    if (!selected?.patientId) return;
    setPatient({ id: selected.patientId, name: selected.patientName || 'Пациент' });
    setSelected(null);
    setLinks(null);
    setLinksError('');
    setCounts(null);
    setCountsError('');
    setListKey((k) => k + 1);
  };

  if (availability === 'loading') {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (availability === 'missing') {
    return (
      <StateBox
        title="Ядро «ГидраМедик» не установлено"
        text="Для вкладки «Записи» нужны документ «Приём» (medic_encounters) и справочник «Пациенты» (medic_patients). Установите поставку hydra.medic.core — записи и пациенты появятся здесь."
        onRetry={retryAvailability}
      />
    );
  }

  if (availability === 'error') {
    return (
      <StateBox
        title="Не удалось проверить ядро «ГидраМедик»"
        text={availabilityError}
        onRetry={retryAvailability}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b bg-background/60 px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <CalendarDays className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Записи</span>
        </div>
        <TabBar
          tabs={periodTabs}
          active={period}
          onChange={(id) => changePeriod(id as AppointmentPeriod)}
          group="cabinet-appointments-period"
        />
        {patient && (
          <span className="inline-flex max-w-[240px] items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-[11px]">
            <span className="truncate" title={patient.name}>{patient.name}</span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              title="Показать все приёмы"
              onClick={() => {
                setPatient(null);
                setSelected(null);
                setLinks(null);
                setLinksError('');
                setCounts(null);
                setCountsError('');
                setListKey((k) => k + 1);
              }}
            >
              ×
            </button>
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={refresh} disabled={countsLoading}>
            <RefreshCw className={`mr-1 h-3 w-3 ${countsLoading ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
          <Button size="sm" className="h-7 px-2 text-[11px]" onClick={openCreate}>
            <Plus className="mr-1 h-3 w-3" />
            Новая запись
          </Button>
        </div>
      </div>

      {countsError && (
        <div className="shrink-0 border-b border-destructive/30 bg-destructive/10 px-3 py-1.5 text-[11px] text-destructive">
          {countsError}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <div className="min-h-0 flex-[1.15] border-b md:border-b-0 md:border-r">
          <AutoListForm
            key={`${period}:${patient?.id || 'all'}:${listKey}`}
            objectType="document"
            objectName={APPOINTMENT_DOCUMENT}
            filters={filters}
            serverFilters={serverFilters}
            visibleKeys={['number', 'appointment_at', 'patient_id', 'status', 'doctor_id']}
            viewMode="list"
            showToolbar={false}
            onSelect={selectRow}
            onRowDoubleClick={(row) => {
              const appointment = appointmentFromListItem(row);
              setSelected(appointment);
              openEdit(appointment);
            }}
          />
        </div>
        <aside className="min-h-0 flex-1">
          <CabinetAppointmentCard
            appointment={selected}
            statusLabels={statusLabels}
            typeLabels={typeLabels}
            links={links}
            linksLoading={linksLoading}
            linksError={linksError}
            psyInstalled={psyInstalled}
            periodCount={periodCount}
            countsLoading={countsLoading}
            onCreate={openCreate}
            onEdit={() => { if (selected) openEdit(selected); }}
            onOpenPatient={() => {
              if (!selected?.patientId) return;
              setCardPatient({
                id: selected.patientId,
                name: selected.patientName || 'Пациент',
              });
            }}
            onOpenConclusions={() => setRelatedMode('conclusions')}
            onOpenTests={() => setRelatedMode('tests')}
            onOpenNote={(note: RelatedNote) => {
              if (!note.id) return;
              setEditor({ objectType: 'catalog', objectName: PSY_NOTES, objectId: note.id });
            }}
            onFilterPatient={filterPatient}
          />
        </aside>
      </div>

      {cardPatient && (
        <UniversalWindow
          open
          onClose={() => setCardPatient(null)}
          title={`Карточка пациента: ${cardPatient.name}`}
          width="w-[min(1100px,96vw)]"
          height="h-[min(88vh,94vh)]"
        >
          <PatientCard
            patientId={cardPatient.id}
            patientName={cardPatient.name}
            onOpenAppointment={(id) => {
              setCardPatient(null);
              setEditor({ objectType: 'document', objectName: APPOINTMENT_DOCUMENT, objectId: id });
            }}
          />
        </UniversalWindow>
      )}

      {editor && (
        <AutoObjectForm
          objectType={editor.objectType}
          objectName={editor.objectName}
          objectId={editor.objectId}
          initialData={editor.initialData}
          onClose={closeEditor}
        />
      )}

      <CabinetAppointmentRelatedDialog
        open={relatedMode !== null}
        mode={relatedMode || 'conclusions'}
        appointment={selected}
        psyInstalled={psyInstalled}
        onClose={() => {
          setRelatedMode(null);
          setLinks(null);
          setLinksError('');
          setLinksKey((k) => k + 1);
        }}
      />
    </div>
  );
}
